<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            Les recherches pour « créer une visite virtuelle immobilière » ont
            explosé de <strong>+4 400 %</strong> au cours des trois dernières
            années. Les annonces avec visite virtuelle reçoivent 87 % de vues
            supplémentaires. Pourtant, nombreux sont les agents qui croient
            encore avoir besoin d'un Matterport à 3 000 € ou d'un photographe
            professionnel. Ce n'est plus le cas — voici comment créer votre
            visite virtuelle immobilière avec votre smartphone.
          </p>
        </section>

        <section>
          <h2>Ce dont vous avez réellement besoin</h2>

          <h3>Équipement minimal</h3>
          <ul>
            <li>iPhone 13+ ou équivalent Android (moins de 4 ans)</li>
            <li>Application Travvir (essai gratuit)</li>
            <li>Éclairage naturel ou lampes d'appoint</li>
            <li>Trépied optionnel (améliore la régularité)</li>
          </ul>

          <h3>Pas de matériel coûteux</h3>
          <p>
            Les caméras 360° dédiées (Ricoh Theta, Insta360) coûtent 300–700 €.
            Un kit Matterport dépasse 3 000 €. Travvir transforme votre
            smartphone existant en outil de visite virtuelle professionnelle
            sans investissement matériel supplémentaire.
          </p>
        </section>

        <section>
          <h2>Préparer le bien avant la séance</h2>

          <p>
            La préparation représente 80 % de la qualité finale. Suivez cette
            checklist avant chaque tournage :
          </p>

          <h3>Désencombrement</h3>
          <p>
            Rangez les objets personnels (photos de famille, courrier,
            médicaments). Les acheteurs doivent se projeter — pas voir la vie du
            vendeur. Libérez les plans de travail et les surfaces visibles.
          </p>

          <h3>Lumière optimale</h3>
          <p>
            Ouvrez tous les stores et rideaux au maximum. Allumez toutes les
            lumières intérieures — même en plein jour. Évitez les journées trop
            ensoleillées qui créent des halos à travers les fenêtres.
          </p>

          <h3>Mise en scène rapide</h3>
          <p>
            Redirigez les meubles écartés vers leur position naturelle. Ajoutez
            des fleurs fraîches ou un bol de fruits dans la cuisine. Ces détails
            coutent moins de 20 € et améliorent significativement les
            conversions.
          </p>
        </section>

        <section>
          <h2>Processus de tournage étape par étape</h2>

          <h3>1. Lancer Travvir et créer un projet</h3>
          <p>
            Ouvrez l'application, appuyez sur « Nouveau projet » et donnez un
            nom à votre bien (ex. : « 12 rue des Lilas — 75020 »). Sélectionnez
            le type de bien dans la liste déroulante.
          </p>

          <h3>2. Capturer chaque pièce dans l'ordre</h3>
          <p>
            Commencez par l'extérieur si le bien en dispose, puis avancez
            logiquement : entrée → salon → cuisine → chambres → salles de bain.
            Positionnez-vous au centre de chaque pièce, tenez le smartphone à
            hauteur d'yeux et tournez selon les instructions de l'application.
          </p>

          <h3>3. Ajouter des scènes supplémentaires si nécessaire</h3>
          <p>
            Pour les grandes pièces en L ou les espaces ouverts, ajoutez une
            seconde scène d'angle. Cela éliminine les zones mortes invisibles
            depuis un seul point de vue.
          </p>

          <h3>4. Laisser Travvir assembler la visite</h3>
          <p>
            Envoyez vos captures — Travvir assemble automatiquement chaque
            panorama et crée les liens de navigation entre scènes. Le traitement
            prend 2 à 5 minutes par scène.
          </p>

          <h3>5. Prévisualiser et peaufiner</h3>
          <p>
            Vérifiez chaque transition, corrigez l'orientation du panorama de
            départ si nécessaire et ajoutez les hotspots de navigation
            manquants.
          </p>
        </section>

        <section>
          <h2>Publication et distribution</h2>
          <p>
            Une fois satisfait du résultat, publiez la visite. Vous obtenez
            instantanément un lien direct et un code iFrame. Partagez-les dans
            votre annonce MLS, sur votre site web, par e-mail aux acheteurs
            potentiels et sur les réseaux sociaux.
          </p>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "creer-visite-virtuelle-immobilier",
);

useBlogSeo({
  title:
    "Comment Créer une Visite Virtuelle Immobilière avec votre Smartphone | Travvir",
  description:
    "Créez une visite virtuelle immobilière professionnelle avec votre smartphone. Checklist de préparation, guide de tournage étape par étape et conseils de publication.",
  keywords:
    "créer visite virtuelle immobilier, visite virtuelle smartphone immobilier, guide visite virtuelle agent",
  slug: "creer-visite-virtuelle-immobilier",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question:
        "Faut-il une caméra 360° pour créer une visite virtuelle immobilière ?",
      answer:
        "Non — un iPhone 13+ ou Android récent suffit avec l'application Travvir. Aucun matériel coûteux n'est nécessaire.",
    },
    {
      question: "Combien de temps faut-il pour créer une visite complète ?",
      answer:
        "Comptez 30 à 60 minutes sur place pour un appartement 3 pièces, puis 10 à 20 minutes de traitement automatique.",
    },
  ],
});

</script>
